import { useRef } from "react";
import { useForm, useWatch, useFieldArray } from "react-hook-form";
import {
  DateField,
  InlineInputField,
  InlineSelect,
  InputField,
  Label,
} from "../../../components/custom/programs/fields";
import { Col, Form, Nav, Row } from "react-bootstrap";
import { BudgetCapFields, CheckboxField, ProductCategoriesField, RadioGroupField, SelectField } from "./fields";
import {
  castNumbersInFormData,
  formatLineItemFormData,
  resolveLineItemFormFieldDependencies,
  validateMandatoryLineItemFormFields,
} from "./utils";
import {
  API_FIELD_MAPPINGS,
  DEVICE_OPTIONS,
  DEVICE_ORIENTATION_OPTIONS,
  FREQUENCY_TIME_UNIT_OPTIONS,
  INVENTORY_TYPE_OPTIONS,
  LEGACY_INVENTORY_TYPE_OPTIONS,
  LINE_ITEM_BID_OPTIMIZATION_STRATEGY_OPTIONS,
  LINE_ITEM_CATCH_UP_BOOST_OPTIONS,
  LINE_ITEM_CREATIVE_ROTATION_TYPE_OPTIONS,
  LINE_ITEM_DELIVERY_PACING_OPTIONS,
  LINE_ITEM_FORM_DEFAULTS,
  LINE_ITEM_MAX_AVERAGE_CPM_OPTIMIZATION_OPTIONS,
  LINE_ITEM_STATE_OPTIONS,
  MOBILE_ENVIRONMENT_OPTIONS,
  MOBILE_OS_OPTIONS,
  PHYSICAL_DEVICE_OPTIONS,
  TIME_ZONE_TYPE_OPTIONS,
  USER_LOCATION_OPTIONS,
  USER_LOCATION_SIGNAL_OPTIONS,
  VIDEO_COMPLETION_TIER_OPTIONS,
  VIEWABILITY_TIER_OPTIONS,
} from "./constants";
import { Button } from "../../../components/core/basic/Button";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { successToast, errorToast } from "../../../utils/toasts";
import { MARKETPLACE_CURRENCY_CODE } from "../../../utils/marketplaceConstants";
import { useAppSelector } from "../../../redux/hooks";
import { useDateFormatter } from "../../../i18n/useDateFormatter";
import moment from "moment";
import useOnScreen from "../../../hooks/useOnScreen";
import { useDSPProductCategories } from "../hooks/useDSPProductCategories";
import {
  AmazonInventoriesField,
  AmazonPublisherDirectInventoriesField,
  AppTargetsField,
  AudienceTargetingField,
  ContextualCategoryTargetsField,
  ContextualKeywordTargetsField,
  ContextualProductTargetsField,
  DomainTargetsField,
  GeoTargetsField,
  IabCategoriesField,
  ThirdPartyInventoriesField,
} from "./targetingFields";
import api from "../../../utils/api";
import Loading from "../../../components/core/blocks/Loading";
import { CreativeSelector } from "./creativeFields";
import { css } from "../../../../styled-system/css";
import { useTranslation } from "react-i18next";

const sectionNavClass = css({
  "& .nav-link:not(.active):hover, & .nav-link:not(.active):focus, & .nav-link:not(.active):focus-visible": {
    borderBottomColor: "transparent",
  },
});

const DSPLineItemForm = ({
  initialFormData = {},
  createMode = false,
  cloneMode = false,
  onCancel = () => {},
  onSuccessfulSubmit = () => {},
  campaignId = null,
  adGroupId = null,
  advertiserId = null,
  orderStartDate,
  orderEndDate,
  isLoading,
}) => {
  const { t } = useTranslation(["dsp"]);
  const fmt = useDateFormatter("monthDay");
  const { marketPlace } = useAppSelector((state) => state.marketPlace);
  const queryClient = useQueryClient();

  const fieldColumnWidth = 8;
  const errorClassNames = "d-block ms-3 mt-2 fs-5";
  const entityId = initialFormData?.id;

  const sections = [
    { key: "overview", label: t(($) => $.dsp["Overview"]) },
    { key: "bidding", label: t(($) => $.dsp["Bidding & Optimization"]) },
    { key: "delivery", label: t(($) => $.dsp["Delivery"]) },
    { key: "products", label: t(($) => $.dsp["Products"]) },
    { key: "targeting", label: t(($) => $.dsp["Targeting"]) },
    { key: "inventory", label: t(($) => $.dsp["Inventory"]) },
    { key: "creatives", label: t(($) => $.dsp["Creatives"]) },
  ];

  const overviewSectionRef = useRef();
  const biddingSectionRef = useRef();
  const deliverySectionRef = useRef();
  const productSectionRef = useRef();
  const targetingSectionRef = useRef();
  const inventorySectionRef = useRef();
  const creativesSectionRef = useRef();
  const scrollContainerRef = useRef(null);

  const visibility = [];
  visibility[0] = useOnScreen(overviewSectionRef);
  visibility[1] = useOnScreen(biddingSectionRef);
  visibility[2] = useOnScreen(deliverySectionRef);
  visibility[3] = useOnScreen(productSectionRef);
  visibility[4] = useOnScreen(targetingSectionRef);
  visibility[5] = useOnScreen(inventorySectionRef);
  visibility[6] = useOnScreen(creativesSectionRef);

  const sectionRefMapping = {
    overview: overviewSectionRef,
    bidding: biddingSectionRef,
    delivery: deliverySectionRef,
    products: productSectionRef,
    targeting: targetingSectionRef,
    inventory: inventorySectionRef,
    creatives: creativesSectionRef,
  };

  const greatestVisibleStep = visibility.lastIndexOf(true);

  const {
    control,
    setValue,
    formState: { errors, dirtyFields },
    reset,
    setError,
    clearErrors,
  } = useForm({
    mode: "onChange",
    defaultValues:
      createMode ?
        {
          ...LINE_ITEM_FORM_DEFAULTS,
          startDateTime:
            moment(orderStartDate).isAfter(moment()) ?
              moment(orderStartDate).toISOString()
            : moment().startOf("hour").add(1, "hour").toISOString(),
          endDateTime:
            moment().add(30, "d").endOf("d").isAfter(moment(orderEndDate)) ?
              moment(orderEndDate).toISOString()
            : moment().add(30, "d").endOf("d"),
        }
      : formatLineItemFormData(initialFormData),
  });

  const formState = useWatch({ control });

  const { data: productCategoryOptions } = useDSPProductCategories();
  const displayInventoryTypeWithMobile =
    formState.inventoryType === "DISPLAY" && formState.mediaDevices?.includes("MOBILE");

  if (!formState.advProductCategoryIds?.length && !errors.advProductCategoryIds) {
    setError("advProductCategoryIds", {
      type: "custom",
      message: t(($) => $.dsp["At least one product category ID is required"]),
    });
  }

  if (formState.advProductCategoryIds?.length && errors.advProductCategoryIds) {
    clearErrors("advProductCategoryIds");
  }

  if (
    (moment(formState.startDateTime).isBefore(moment(orderStartDate)) ||
      (moment(formState.startDateTime).isBefore(moment()) && !entityId)) &&
    !errors.startDateTime
  ) {
    setError("startDateTime", {
      type: "custom",
      message: t(($) => $.dsp["Start date must be after {date}"], {
        date: fmt.format(moment.max([moment(orderStartDate), moment()]).toDate()),
      }),
    });
  }

  if (
    moment(formState.endDateTime).isBefore(moment(formState.startDateTime).add(1, "day")) &&
    !errors.endDateTime &&
    !errors.startDateTime
  ) {
    setError("endDateTime", {
      type: "custom",
      message: t(($) => $.dsp["End date must be after {date}"], {
        date: fmt.format(moment(formState.startDateTime).toDate()),
      }),
    });
  }

  if (moment(formState.endDateTime).isAfter(moment(orderEndDate)) && !errors.endDateTime) {
    setError("endDateTime", {
      type: "custom",
      message: t(($) => $.dsp["Start date must be before {date}"], {
        date: fmt.format(moment(orderEndDate).toDate()),
      }),
    });
  }

  const combinedInventoryCount = [
    ...(formState.amazonInventories ?? []),
    ...(formState.thirdPartyInventories ?? []),
    ...(formState.amazonPublisherDirectInventories ?? []),
  ]?.length;

  if (combinedInventoryCount < 1 && !errors.inventoryCount) {
    setError("inventoryCount", {
      type: "custom",
      message: t(($) => $.dsp["At least one inventory must be selected."]),
    });
  }

  if (errors.inventoryCount && combinedInventoryCount) {
    clearErrors("inventoryCount");
  }

  const {
    fields: budgetCapFields,
    append: appendBudgetCap,
    remove: removeBudgetCap,
  } = useFieldArray({
    control,
    name: "budgetCaps",
  });

  resolveLineItemFormFieldDependencies(formState, budgetCapFields, setValue);

  const checkDirtyFields = (fieldNames) => {
    return Object.keys(dirtyFields).some((key) => fieldNames.includes(key));
  };

  const updateLineItem = useMutation({
    mutationFn: async () => {
      let updateData = {};
      Object.keys(dirtyFields).forEach((dirtyFieldKey) => {
        if (dirtyFieldKey in API_FIELD_MAPPINGS) {
          updateData[API_FIELD_MAPPINGS[dirtyFieldKey]] = formState[dirtyFieldKey];
        }
      });

      // Handle advProductCategoryIds difference manually for now, isDirty not getting set in rhf for some reason

      if (!(
        formState.advProductCategoryIds?.length === initialFormData.advertised_product_category_ids?.length &&
        formState.advProductCategoryIds?.every((id) => initialFormData.advertised_product_category_ids?.includes(id))
      )) {
        updateData["advertised_product_category_ids"] = formState["advProductCategoryIds"];
      }

      if (updateData.optimization?.automateBudgetAllocation) {
        delete updateData.budget_amount;
      }

      updateData = castNumbersInFormData(updateData);

      const processLineItemUpdates = Object.keys(updateData).length;

      let res;

      if (processLineItemUpdates) {
        res = await axios.patch(`/dsp/lineitem/${entityId}/`, updateData);
      }

      if ((processLineItemUpdates && res?.status === 200) || !processLineItemUpdates) {
        const savedTargets = initialFormData.targets ?? [];
        upsertLineItemTargets.mutate({ lineItemId: entityId, savedTargets });
        if (checkDirtyFields(["creativeAssociations"])) {
          upsertLineItemCreatives.mutate({ adGroupId });
        }
      }
      return res;
    },

    onSuccess: () => {
      reset({}, { keepValues: true });
      onSuccessfulSubmit();
      successToast({
        message: t(($) => $.dsp["Line item successfully updated"]),
      });
    },

    onError: (error) => {
      errorToast({
        message: t(($) => $.dsp["Failed to update line item"]),
        error,
      });
    },
  });

  const upsertLineItemCreatives = useMutation({
    mutationFn: async ({ adGroupId }) => {
      const creativeAssociationBody = {
        advertiser_id: advertiserId,
        ad_group_id: adGroupId,
        body: {
          ad_creative_ids: formState.creativeAssociations.map(
            (creativeAssociation) => creativeAssociation.ad_creative_id,
          ),
          ad_group_id: adGroupId,
        },
      };
      const res = await api.post("/dsp/adcreativeassociation/", creativeAssociationBody);

      if (res.status === 201) {
        queryClient.invalidateQueries({
          queryKey: ["dspLineItemCreativeAssociations", adGroupId],
        });
      }

      return res;
    },
  });

  const upsertLineItemTargets = useMutation({
    mutationFn: async ({ lineItemId }) => {
      const formatTargets = ({ formTargets = [], formExclusions = [], targetType, detailKey, postSubtype = null }) => {
        const TARGET_TYPE_CONFIG_LOOKUP = {
          AUDIENCE: { key: "audienceId" },
          LOCATION: { key: "geoLocation" },
          APP: { key: "appId", additionalParams: { appType: "MOBILE" } },
          DOMAIN: { key: "domainName", additionalParams: { domainTargetType: "DOMAIN_NAME" } },
          IAB_CATEGORY: { key: "iabContentCategory" },
          PRODUCT: {
            key: "asin",
            additionalParams: {
              matchType: "PRODUCT_EXACT",
            },
          },
          PRODUCT_CATEGORY: {
            key: "asinCategory",
          },
          KEYWORD: {
            key: "keyword",
            additionalParams: {
              matchType: "BROAD",
            },
          },
          DEVICE: {
            key:
              postSubtype === "physicalDeviceTargets" ? "mobileDevice"
              : postSubtype === "deviceOrientationTargets" ? "deviceOrientation"
              : postSubtype === "mobileOperatingSystem" ? "mobileOS"
              : "",
            additionalParams:
              postSubtype === "physicalDeviceTargets" ? { deviceType: "MOBILE", mobileEnvironment: "APP" }
              : postSubtype === "deviceOrientationTargets" ?
                { deviceType: "MOBILE", mobileEnvironment: "APP", mobileDevice: null }
              : postSubtype === "mobileOperatingSystem" ? { deviceType: "MOBILE", mobileEnvironment: "WEB" }
              : {},
          },
          AUTO: {
            key: "",
            additionalParams: {
              matchType: "ASIN_RELATED",
            },
          },
          INVENTORY_SOURCE: {
            key: "inventorySourceId",
            additionalParams:
              postSubtype === "amazonInventories" ? { inventorySourceType: "AMAZON" }
              : postSubtype === "thirdPartyInventories" ? { inventorySourceType: "THIRD_PARTY_EXCHANGE" }
              : postSubtype === "amazonPublisherDirectInventories" ? { inventorySourceType: "APD" }
              : {},
          },
        };

        const targets = [];
        const targetTypeConfig = TARGET_TYPE_CONFIG_LOOKUP[targetType];
        formTargets.forEach((targetSpec) => {
          if (postSubtype === "deviceOrientationTargets") {
            formState.deviceTargets?.forEach((deviceType) => {
              targets.push({
                line_item: lineItemId,
                target_details: {
                  [detailKey]: {
                    [targetTypeConfig.key]: targetSpec,
                    deviceType: "MOBILE",
                    mobileEnvironment: "APP",
                    mobileDevice: deviceType,
                    targetType,
                  },
                },
                negative: false,
              });
            });
          } else {
            targets.push({
              line_item: lineItemId,
              target_details: {
                [detailKey]:
                  targetType === "AUDIENCE" ? targetSpec
                  : targetType === "DOMAIN" ?
                    {
                      targetType,
                      domainTargetDetails: {
                        domainName: {
                          [targetTypeConfig.key]: targetSpec,
                          ...targetTypeConfig.additionalParams,
                        },
                      },
                    }
                  : {
                      [targetTypeConfig.key]: targetSpec,
                      ...targetTypeConfig.additionalParams,
                      targetType,
                    },
              },
              negative: false,
            });
          }
        });

        formExclusions.forEach((exclusionSpec) => {
          targets.push({
            line_item: lineItemId,
            target_details: {
              [detailKey]:
                targetType === "AUDIENCE" ? exclusionSpec
                : targetType === "DOMAIN" ?
                  {
                    targetType,
                    domainTargetDetails: {
                      domainName: {
                        [targetTypeConfig.key]: exclusionSpec,
                        ...targetTypeConfig.additionalParams,
                      },
                    },
                  }
                : {
                    [targetTypeConfig.key]: exclusionSpec,
                    ...targetTypeConfig.additionalParams,
                    targetType,
                  },
            },
            negative: true,
          });
        });

        return targets;
      };

      const processTargets = async ({ formTargets = [], formExclusions = [], targetType, detailKey, postType }) => {
        const targets = formatTargets({
          formTargets,
          formExclusions,
          targetType,
          detailKey,
        });

        return api.post("/dsp/target/", {
          target_type: postType,
          advertiser_id: advertiserId,
          line_item: lineItemId,
          body: targets,
        });
      };

      const targetTypeMutations = [];
      let deviceTargets = [];

      if (["AAP_MOBILE_APP"].includes(formState.inventoryType) || displayInventoryTypeWithMobile) {
        deviceTargets = [
          ...deviceTargets,
          ...formatTargets({
            formTargets: formState.deviceTargets?.includes("ALL") ? [] : formState.deviceTargets,
            formExclusions: [],
            targetType: "DEVICE",
            detailKey: "deviceTarget",
            postSubtype: "physicalDeviceTargets",
          }),
        ];

        deviceTargets = [
          ...deviceTargets,
          ...formatTargets({
            formTargets: formState.deviceOrientationTarget === "ALL" ? [] : [formState.deviceOrientationTarget],
            formExclusions: [],
            targetType: "DEVICE",
            detailKey: "deviceTarget",
            postSubtype: "deviceOrientationTargets",
          }),
        ];
      }

      if (
        ["STANDARD_DISPLAY", "AMAZON_MOBILE_DISPLAY"].includes(formState.inventoryType) ||
        displayInventoryTypeWithMobile
      ) {
        deviceTargets = [
          ...deviceTargets,
          ...formatTargets({
            formTargets: formState.mobileOperatingSystem === "ALL" ? [] : [formState.mobileOperatingSystem],
            formExclusions: [],
            targetType: "DEVICE",
            detailKey: "deviceTarget",
            postSubtype: "mobileOperatingSystem",
          }),
        ];
      }
      if (["DISPLAY"].includes(formState.inventoryType)) {
        if (formState.mediaDevices?.includes("DESKTOP")) {
          deviceTargets.push({
            line_item: lineItemId,
            target_details: {
              deviceTarget: {
                deviceType: "DESKTOP",
                targetType: "DEVICE",
              },
            },
            negative: false,
          });
        }
        if (formState.mediaDevices?.includes("MOBILE")) {
          formState.mediaMobileEnvironments?.forEach((environment) => {
            deviceTargets.push({
              line_item: lineItemId,
              target_details: {
                deviceTarget: {
                  deviceType: "MOBILE",
                  mobileEnvironment: environment,
                  targetType: "DEVICE",
                },
              },
              negative: false,
            });
          });
        }
      }

      targetTypeMutations.push(
        api.post("/dsp/target/", {
          target_type: "deviceTarget",
          advertiser_id: advertiserId,
          line_item: lineItemId,
          body: deviceTargets,
        }),
      );

      // Audience targets from the API contain read-only fields that will fail to update if sent, so only send audience target updates
      // when altered in widget
      if (checkDirtyFields(["audienceGroupExclusions", "audienceGroupTargets"]) || cloneMode) {
        targetTypeMutations.push(
          processTargets({
            formTargets: formState.audienceGroupTargets ?? [],
            formExclusions: formState.audienceGroupExclusions ?? [],
            targetType: "AUDIENCE",
            detailKey: "audienceTarget",
            postType: "audienceTarget",
          }),
        );
      }

      if (
        (["AAP_MOBILE_APP"].includes(formState.inventoryType) || displayInventoryTypeWithMobile) &&
        (cloneMode || checkDirtyFields(["appTargetIds", "excludedAppTargetIds"]))
      ) {
        targetTypeMutations.push(
          processTargets({
            formTargets: formState.appTargetIds ?? [],
            formExclusions: formState.excludedAppTargetIds ?? [],
            targetType: "APP",
            detailKey: "appTarget",
            postType: "appTarget",
          }),
        );
      }

      if (
        ["STANDARD_DISPLAY", "VIDEO", "DISPLAY"].includes(formState.inventoryType) &&
        (cloneMode || checkDirtyFields(["iabCategoryTargetIds"]))
      ) {
        targetTypeMutations.push(
          processTargets({
            formTargets: formState.iabCategoryTargetIds ?? [],
            formExclusions: [],
            targetType: "IAB_CATEGORY",
            detailKey: "iabCategoryTarget",
            postType: "iabCategoryTarget",
          }),
        );
      }

      if (checkDirtyFields(["targetDomains", "excludedTargetDomains"]) || cloneMode)
        targetTypeMutations.push(
          processTargets({
            formTargets: formState.targetDomains ?? [],
            formExclusions: formState.excludedTargetDomains ?? [],
            targetType: "DOMAIN",
            detailKey: "domainTarget",
            postType: "domainTarget",
          }),
        );

      if (["STANDARD_DISPLAY", "AMAZON_MOBILE_DISPLAY", "DISPLAY"].includes(formState.inventoryType)) {
        if (checkDirtyFields(["contextualProductTargetIds", "excludedContextualProductTargetIds"]) || cloneMode) {
          targetTypeMutations.push(
            processTargets({
              formTargets: formState.contextualProductTargetIds ?? [],
              formExclusions: formState.excludedContextualProductTargetIds ?? [],
              targetType: "PRODUCT",
              detailKey: "productTarget",
              postType: "productTarget",
            }),
          );
        }

        if (checkDirtyFields(["contextualCategoryTargetIds", "excludedContextualCategoryTargetIds"]) || cloneMode) {
          targetTypeMutations.push(
            processTargets({
              formTargets: formState.contextualCategoryTargetIds ?? [],
              formExclusions: formState.excludedContextualCategoryTargetIds ?? [],
              targetType: "PRODUCT_CATEGORY",
              detailKey: "productCategoryTarget",
              postType: "productCategoryTarget",
            }),
          );
        }

        if (checkDirtyFields(["contextualKeywords", "excludedContextualKeywords"]) || cloneMode) {
          targetTypeMutations.push(
            processTargets({
              formTargets: formState.contextualKeywords ?? [],
              formExclusions: formState.excludedContextualKeywords ?? [],
              targetType: "KEYWORD",
              detailKey: "keywordTarget",
              postType: "keywordTarget",
            }),
          );
        }

        if (checkDirtyFields(["enableRelatedProductsTargeting"]) || cloneMode) {
          targetTypeMutations.push(
            processTargets({
              formTargets: formState.enableRelatedProductsTargeting ? [true] : [],
              formExclusions: [],
              targetType: "AUTO",
              detailKey: "autoTarget",
              postType: "autoTarget",
            }),
          );
        }
      }

      if (checkDirtyFields(["geoTargetIds", "excludedGeoTargetIds"]) || cloneMode) {
        targetTypeMutations.push(
          processTargets({
            formTargets: formState.geoTargetIds ?? [],
            formExclusions: formState.excludedGeoTargetIds ?? [],
            targetType: "LOCATION",
            detailKey: "locationTarget",
            postType: "locationTarget",
          }),
        );
      }

      let inventorySourceTargets = [];

      if (
        checkDirtyFields(["amazonInventories", "thirdPartyInventories", "amazonPublisherDirectInventories"]) ||
        cloneMode
      ) {
        inventorySourceTargets = formatTargets({
          formTargets: formState.amazonInventories ?? [],
          formExclusions: [],
          targetType: "INVENTORY_SOURCE",
          detailKey: "inventorySourceTarget",
          postSubtype: "amazonInventories",
        });

        inventorySourceTargets = [
          ...inventorySourceTargets,
          ...formatTargets({
            formTargets: formState.thirdPartyInventories ?? [],
            formExclusions: [],
            targetType: "INVENTORY_SOURCE",
            detailKey: "inventorySourceTarget",
            postSubtype: "thirdPartyInventories",
          }),
        ];

        inventorySourceTargets = [
          ...inventorySourceTargets,
          ...formatTargets({
            formTargets: formState.amazonPublisherDirectInventories ?? [],
            formExclusions: [],
            targetType: "INVENTORY_SOURCE",
            detailKey: "inventorySourceTarget",
            postSubtype: "amazonPublisherDirectInventories",
          }),
        ];
        targetTypeMutations.push(
          api.post("/dsp/target/", {
            target_type: "inventorySourceTarget",
            advertiser_id: advertiserId,
            line_item: lineItemId,
            body: inventorySourceTargets,
          }),
        );
      }

      const mutationResults = await Promise.allSettled(targetTypeMutations);
      const errors = mutationResults.filter((result) => result.status === "rejected");

      queryClient.invalidateQueries();

      if (errors.length > 0) {
        throw new Error(t(($) => $.dsp["Error while updating line item targets."]));
      }
    },

    onSuccess: () => {
      successToast({
        message: t(($) => $.dsp["Successfully updated line item targets."]),
      });
    },

    onError: (error) => {
      errorToast({
        message: t(($) => $.dsp["Error while updating line item targets."]),
        error,
      });
    },
  });

  const createLineItem = useMutation({
    mutationFn: async () => {
      const fields = [
        "inventoryType",
        "comments",
        "creativeRotationType",
        "endDateTime",
        "startDateTime",
        "advProductCategoryIds",
        "budgetCaps",
        "pacing",
        "targetingSettings",
        "budgetAmount",
        "optimization",
        "name",
        "state",
        "bid",
        "frequencies",
      ];

      let createData = {};

      fields.forEach((key) => {
        createData[API_FIELD_MAPPINGS[key]] = formState[key];
      });

      if (createData.optimization.automateBudgetAllocation) {
        delete createData.budget_amount;
      }
      // Final processing of form data => cast numbers and add missing information needed by API
      createData = castNumbersInFormData(createData);
      createData.bid["currencyCode"] = MARKETPLACE_CURRENCY_CODE[marketPlace];
      createData.campaign_id = campaignId;
      createData.advertiser_id = advertiserId;

      const res = await axios.post(`/dsp/lineitem/`, createData);

      if (res.status === 201) {
        queryClient.invalidateQueries({
          predicate: (query) => ["dspLineItemData", "dspLineItemDatasets"].some((key) => query.queryKey[0] === key),
        });
        // Create targets and creatives with new line item ID
        const lineItemId = res.data.id;
        const newAdGroupId = res.data.ad_group_id;
        upsertLineItemTargets.mutate({ lineItemId });
        if (checkDirtyFields(["creativeAssociations"]) || cloneMode) {
          upsertLineItemCreatives.mutate({ adGroupId: newAdGroupId });
        }
      }
      return res;
    },

    onSuccess: () => {
      reset({}, { keepValues: true });
      onSuccessfulSubmit();
      successToast({
        message: t(($) => $.dsp["Line item successfully created"]),
      });
    },

    onError: (error) => {
      errorToast({
        message: t(($) => $.dsp["Failed to create line item"]),
        error,
      });
    },
  });

  return (
    <>
      {isLoading ?
        <Loading />
      : <Row>
          <Col xs={12} lg={3} style={{ borderRight: "1px solid #ccc" }}>
            <Nav
              variant="underline"
              onSelect={(tabId) => {
                const section = sectionRefMapping[tabId]?.current;
                const container = scrollContainerRef.current;
                if (!section || !container) return;
                container.scrollTo({
                  top: section.offsetTop - container.offsetTop,
                  behavior: "smooth",
                });
              }}
              activeKey={sections[greatestVisibleStep]?.key ?? "overview"}
              className={`flex-column text-xs-center text-md-start ${sectionNavClass}`}
            >
              {sections.map(({ key, label }) => (
                <Nav.Link eventKey={key} key={key} className="text-xs-center fs-4 text-md-start py-2">
                  {label}
                </Nav.Link>
              ))}
            </Nav>
          </Col>
          <Col
            ref={scrollContainerRef}
            xs={12}
            lg={9}
            className={"p-5 custom-scroll-container"}
            style={{ overflowY: "auto", maxHeight: "90vh", position: "relative" }}
          >
            {createMode && (
              <Row>
                <h2 className="fw-bold fs-1 ps-2">{t(($) => $.dsp["Create a New Line Item"])}</h2>
              </Row>
            )}
            <form
              onSubmit={(e) => {
                e.preventDefault();
              }}
            >
              {/* Config */}
              <Row className="py-3 dsp-form-section" ref={overviewSectionRef}>
                <Form.Label className="fw-bold fs-2 px-2">{t(($) => $.dsp["Overview"])}</Form.Label>
                <Row className="py-3">
                  <Col xs={12} lg={8} xl={fieldColumnWidth}>
                    <InputField
                      label={t(($) => $.dsp["Name"])}
                      placeholder={t(($) => $.dsp["Name"])}
                      fieldName="name"
                      errors={errors}
                      control={control}
                      rules={{
                        required: { value: true, message: t(($) => $.dsp["Name is required"]) },
                        minLength: {
                          value: 2,
                          message: t(($) => $.dsp["Name must be at least 1 character in length"]),
                        },
                        maxLength: {
                          value: 255,
                          message: t(($) => $.dsp["Name must be 255 characters or less in length"]),
                        },
                      }}
                      errorClassNames={errorClassNames}
                    />
                  </Col>
                </Row>

                {!createMode ?
                  <Row className="py-3">
                    <Col xs={12} lg={8} xl={fieldColumnWidth}>
                      <SelectField
                        label={t(($) => $.dsp["State"])}
                        options={LINE_ITEM_STATE_OPTIONS}
                        fieldName={"state"}
                        control={control}
                      />
                    </Col>
                  </Row>
                : null}
                <Row className="py-3">
                  <Col xs={12} lg={8} xl={fieldColumnWidth}>
                    <SelectField
                      label={t(($) => $.dsp["Inventory Type"])}
                      // Below is to still show inventory type for legacy line items
                      options={createMode ? INVENTORY_TYPE_OPTIONS : LEGACY_INVENTORY_TYPE_OPTIONS}
                      fieldName={"inventoryType"}
                      control={control}
                      disabled={!createMode}
                    />
                  </Col>
                </Row>
                {["DISPLAY"].includes(formState.inventoryType) ?
                  <>
                    <Row className="py-3">
                      <Col xs={12} lg={11}>
                        <RadioGroupField
                          label={t(($) => $.dsp["Devices"])}
                          options={DEVICE_OPTIONS}
                          fieldName={"mediaDevices"}
                          control={control}
                          deriveFieldValue={(value) => {
                            return formState.mediaDevices?.includes(value);
                          }}
                          onChange={(value, checked) => {
                            let updatedDevices = formState.mediaDevices ?? [];

                            if (checked) {
                              updatedDevices.push(value);
                            } else {
                              updatedDevices = updatedDevices.filter((device) => device !== value);
                            }

                            setValue("mediaDevices", updatedDevices);
                          }}
                        />
                      </Col>
                    </Row>
                    {formState.mediaDevices?.includes("MOBILE") ?
                      <Row className="py-3">
                        <Col xs={12} lg={11}>
                          <RadioGroupField
                            label={t(($) => $.dsp["Mobile Environments"])}
                            options={MOBILE_ENVIRONMENT_OPTIONS}
                            fieldName={"mediaMobileEnvironments"}
                            control={control}
                            deriveFieldValue={(value) => {
                              return formState.mediaMobileEnvironments?.includes(value);
                            }}
                            onChange={(value, checked) => {
                              let updatedEnvironments = formState.mediaMobileEnvironments ?? [];

                              if (checked) {
                                updatedEnvironments.push(value);
                              } else {
                                updatedEnvironments = updatedEnvironments.filter((env) => env !== value);
                              }

                              setValue("mediaMobileEnvironments", updatedEnvironments);
                            }}
                          />
                        </Col>
                      </Row>
                    : null}
                  </>
                : null}

                <Row className="py-3">
                  <Col xs={12} lg={8} xl={fieldColumnWidth}>
                    <InputField
                      label={
                        <>
                          {t(($) => $.dsp["Comments"])} <small style={{ fontWeight: 400 }}>(optional)</small>
                        </>
                      }
                      fieldName="comments"
                      errors={errors}
                      control={control}
                      rules={{
                        maxLength: {
                          value: 255,
                          message: t(($) => $.dsp["Comments must be 255 characters or less in length"]),
                        },
                      }}
                      errorClassNames={errorClassNames}
                    />
                  </Col>
                </Row>
              </Row>
              {/* Bidding */}
              <Row className="py-3 dsp-form-section" ref={biddingSectionRef}>
                <Form.Label className="fw-bold fs-2 px-2">{t(($) => $.dsp["Bidding & Optimization"])}</Form.Label>
                <Row className="py-3">
                  <Col xs={12} lg={8} xl={fieldColumnWidth}>
                    <InputField
                      label={t(($) => $.dsp["Base Bid"])}
                      fieldName={"bid.baseBid"}
                      control={control}
                      type="number"
                      step={0.01}
                      errorClassNames={errorClassNames}
                      errors={errors}
                      rules={{
                        required: {
                          value: true,
                          message: t(($) => $.dsp["This field is required"]),
                        },
                      }}
                    />
                  </Col>
                </Row>
                <Row className="py-3">
                  <Col xs={12} lg={8} xl={fieldColumnWidth}>
                    <SelectField
                      label={t(($) => $.dsp["Maximum Average CPM"])}
                      options={LINE_ITEM_MAX_AVERAGE_CPM_OPTIMIZATION_OPTIONS}
                      fieldName={"optimizeMaxAverageCPM"}
                      control={control}
                    />
                  </Col>
                </Row>
                {formState.optimizeMaxAverageCPM === false && (
                  <Row className="py-3">
                    <Col xs={12} lg={8} xl={fieldColumnWidth}>
                      <InputField
                        label={t(($) => $.dsp["Maximum Average CPM Amount"])}
                        fieldName={"bid.maxAverageCPM"}
                        control={control}
                        type="number"
                        step={0.01}
                        errorClassNames="d-block ms-2 mt-0"
                      />
                    </Col>
                  </Row>
                )}
                <Row className="py-3">
                  <Col xs={12} lg={8} xl={fieldColumnWidth}>
                    <SelectField
                      label={t(($) => $.dsp["Bid Optimization Strategy"])}
                      options={LINE_ITEM_BID_OPTIMIZATION_STRATEGY_OPTIONS}
                      fieldName={"optimization.bidStrategy"}
                      control={control}
                    />
                  </Col>
                </Row>
              </Row>
              {/* Delivery */}
              <Row className="py-3 dsp-form-section" ref={deliverySectionRef}>
                <Form.Label className="fw-bold fs-2 px-2">{t(($) => $.dsp["Delivery"])}</Form.Label>
                <Row className="py-3">
                  <Col xs={12} lg={8} xl={fieldColumnWidth / 2}>
                    <DateField
                      label={t(($) => $.dsp["Start Date"])}
                      placeholder={t(($) => $.dsp["Starting on..."])}
                      fieldName="startDateTime"
                      errors={errors}
                      control={control}
                      rules={{
                        required: { value: true, message: t(($) => $.dsp["Start date is required"]) },
                      }}
                      errorClassNames={errorClassNames}
                      minDate={moment(orderStartDate).add(1, "day").format("YYYY-MM-DD")}
                      maxDate={moment(orderEndDate).format("YYYY-MM-DD")}
                      showTimeInput={true}
                    />
                  </Col>
                  <Col xs={12} lg={8} xl={fieldColumnWidth / 2}>
                    <DateField
                      label={t(($) => $.dsp["End Date"])}
                      placeholder={t(($) => $.dsp["Ending on..."])}
                      fieldName="endDateTime"
                      errors={errors}
                      control={control}
                      rules={{
                        required: { value: true, message: t(($) => $.dsp["An end date is required"]) },
                      }}
                      errorClassNames={errorClassNames}
                      minDate={moment(formState.startDateTime).add(2, "days").format("YYYY-MM-DD")}
                      maxDate={moment(orderEndDate).format("YYYY-MM-DD")}
                      showTimeInput={true}
                    />
                  </Col>
                </Row>
                <Row className="py-3">
                  <Col xs={12} lg={8} xl={fieldColumnWidth}>
                    <CheckboxField
                      label={t(($) => $.dsp["Automate Budget Allocation"])}
                      fieldName={"optimization.automateBudgetAllocation"}
                      control={control}
                      onChange={(value) => {
                        if (!value) {
                          setValue("budgetAmount", initialFormData.budgetAmount, { shouldDirty: true });
                        }
                      }}
                    />
                  </Col>
                </Row>
                {!formState.optimization?.automateBudgetAllocation && (
                  <Row className="py-3">
                    <Col xs={12} lg={8} xl={fieldColumnWidth}>
                      <InputField
                        label={t(($) => $.dsp["Budget Amount"])}
                        fieldName={"budgetAmount"}
                        control={control}
                        errorClassNames="d-block ms-2 mt-0"
                      />
                    </Col>
                  </Row>
                )}
                <Row className="py-3">
                  <Col xs={12} lg={8} xl={fieldColumnWidth}>
                    <Form.Group>
                      <Label>{t(($) => $.dsp["Budget Caps"])}</Label>
                      <CheckboxField
                        label={t(($) => $.dsp["Set a daily or monthly spending limit"])}
                        fieldName={"budgetCapsEnabled"}
                        control={control}
                        onChange={(value) => {
                          if (value) {
                            appendBudgetCap({ amount: 10, recurrenceTimePeriod: "DAILY" });
                          } else {
                            removeBudgetCap();
                          }
                        }}
                      />
                    </Form.Group>
                  </Col>
                </Row>
                {budgetCapFields.length ?
                  budgetCapFields.map((field, index) => {
                    return (
                      <BudgetCapFields
                        key={field.id}
                        control={control}
                        fieldName={`budgetCaps.${index}`}
                        errors={errors}
                        removeCap={() => {
                          removeBudgetCap(index);
                        }}
                        appendCap={() => {
                          appendBudgetCap({
                            amount: 10,
                            recurrenceTimePeriod:
                              formState.budgetCaps?.[0]?.recurrenceTimePeriod === "DAILY" ? "MONTHLY" : "DAILY",
                          });
                        }}
                        budgetCapCount={budgetCapFields.length}
                        {...field}
                        createMode={createMode}
                        lineItem={true}
                      />
                    );
                  })
                : null}
                <Row className="py-3">
                  <Col xs={12} lg={8} xl={fieldColumnWidth}>
                    <SelectField
                      label={t(($) => $.dsp["Pacing"])}
                      options={LINE_ITEM_DELIVERY_PACING_OPTIONS}
                      fieldName={"pacing.deliveryProfile"}
                      control={control}
                    />
                  </Col>
                </Row>

                {formState.pacing?.deliveryProfile === "EVEN" && (
                  <Row className="py-3">
                    <Col xs={12} lg={8} xl={fieldColumnWidth}>
                      <SelectField
                        label={t(($) => $.dsp["Catch-up Boost %"])}
                        options={LINE_ITEM_CATCH_UP_BOOST_OPTIONS}
                        fieldName={"pacing.catchUpBoostPercentage"}
                        control={control}
                      />
                    </Col>
                  </Row>
                )}
                <Row className="py-3">
                  <Col xs={12} lg={8} xl={fieldColumnWidth}>
                    <SelectField
                      label={t(($) => $.dsp["Creative Rotation Type"])}
                      options={LINE_ITEM_CREATIVE_ROTATION_TYPE_OPTIONS}
                      fieldName={"creativeRotationType"}
                      control={control}
                    />
                  </Col>
                </Row>
                <Row className="py-3">
                  <Col xs={12} lg={8} xl={fieldColumnWidth}>
                    <Form.Group>
                      <Label>{t(($) => $.dsp["Frequencies"])}</Label>
                      <CheckboxField
                        label={t(($) => $.dsp["Limit the number of times a shopper can view this ad"])}
                        fieldName={"frequencyCapped"}
                        control={control}
                        onChange={(value) => {
                          if (value) {
                            setValue("frequencies.0.frequencyType", "CUSTOM", { shouldDirty: true });
                            setValue("frequencies.0.maxImpressions", 5);
                            setValue("frequencies.0.timeUnitCount", 1);
                            setValue("frequencies.0.timeUnit", FREQUENCY_TIME_UNIT_OPTIONS[0].value);
                          } else {
                            setValue("frequencies.0.frequencyType", "UNCAPPED", { shouldDirty: true });
                            setValue("frequencies.0.maxImpressions", null);
                            setValue("frequencies.0.timeUnitCount", null);
                            setValue("frequencies.0.timeUnit", null);
                          }
                        }}
                      />
                    </Form.Group>
                  </Col>
                </Row>
                {formState.frequencyCapped && (
                  <Row className="p-3">
                    <Col xs={12} lg={8} className="d-flex align-items-center gap-3 fs-5">
                      {t(($) => $.dsp["No more than"])}{" "}
                      <InlineInputField
                        fieldName={`frequencies.0.maxImpressions`}
                        control={control}
                        rules={{
                          required: formState.frequencyCapped,
                          min: {
                            value: 1,
                            message: t(($) => $.dsp["Max impressions must be at least 1"]),
                          },
                          max: {
                            value: 99000,
                            message: t(($) => $.dsp["Max impressions cannot be greater than 99000"]),
                          },
                        }}
                        width="125px"
                        errors={errors}
                        placeholder={null}
                        format={"number"}
                      />{" "}
                      {t(($) => $.dsp["times in"])}{" "}
                      <InlineInputField
                        fieldName={`frequencies.0.timeUnitCount`}
                        control={control}
                        rules={{
                          required: formState.frequencyCapped,
                          min: {
                            value: 1,
                            message: t(($) => $.dsp["Count must be at least 1"]),
                          },
                          max: {
                            value: 60,
                            message: t(($) => $.dsp["Count cannot be greater than 60"]),
                          },
                        }}
                        width="125px"
                        errors={errors}
                        placeholder={null}
                        format={"number"}
                      />{" "}
                      <InlineSelect
                        options={FREQUENCY_TIME_UNIT_OPTIONS.map((option) => ({ ...option, label: t(option.label) }))}
                        fieldName={`frequencies.0.timeUnit`}
                        control={control}
                        rules={{ required: true }}
                      />
                    </Col>
                  </Row>
                )}
              </Row>
              <Row className="py-3 dsp-form-section" ref={productSectionRef}>
                {/* Products */}
                <Form.Label className="fw-bold fs-2 px-2">{t(($) => $.dsp["Products"])}</Form.Label>
                <Row className="py-3">
                  <Col xs={12} lg={8} xl={fieldColumnWidth}>
                    <ProductCategoriesField
                      categoryOptions={productCategoryOptions}
                      fieldName={`advProductCategoryIds`}
                      lineItemCategories={formState.advProductCategoryIds}
                      onAddCategory={(id) => {
                        const updateCategories = [...(formState.advProductCategoryIds ?? []), id];
                        setValue("advProductCategoryIds", updateCategories);
                      }}
                      onRemoveCategory={(id) => {
                        const updateCategories = formState.advProductCategoryIds?.filter((c) => c !== id);
                        setValue("advProductCategoryIds", updateCategories);
                      }}
                      onResetCategorySelections={() => {
                        setValue("advProductCategoryIds", initialFormData.advertised_product_category_ids);
                      }}
                      errors={errors}
                    />
                  </Col>
                </Row>
              </Row>
              {/* Targeting */}
              <Row className="py-3 dsp-form-section" ref={targetingSectionRef}>
                <Form.Label className="fw-bold fs-2 px-2">{t(($) => $.dsp["Targeting"])}</Form.Label>
                {formState.inventoryType === "VIDEO" && (
                  <Row className="py-3">
                    <Col xs={12} lg={8} xl={fieldColumnWidth}>
                      <SelectField
                        label={t(($) => $.dsp["Video Completion Tier"])}
                        options={VIDEO_COMPLETION_TIER_OPTIONS}
                        fieldName={"targetingSettings.videoCompletionTier"}
                        control={control}
                      />
                    </Col>
                  </Row>
                )}
                <Row className="py-3">
                  <Col xs={12} lg={8} xl={fieldColumnWidth}>
                    <SelectField
                      label={t(($) => $.dsp["Viewability Tier"])}
                      options={VIEWABILITY_TIER_OPTIONS}
                      fieldName={"targetingSettings.amazonViewability.viewabilityTier"}
                      control={control}
                    />
                  </Col>
                </Row>
                <Row className="py-3">
                  <AudienceTargetingField
                    formState={formState}
                    setValue={setValue}
                    createMode={createMode}
                    entityId={entityId}
                  />
                </Row>
                {["AAP_MOBILE_APP"].includes(formState.inventoryType) || displayInventoryTypeWithMobile ?
                  <Row className="py-3">
                    <AppTargetsField
                      lineItemId={createMode ? null : entityId}
                      formState={formState}
                      initialFormData={initialFormData}
                      setValue={setValue}
                    />
                  </Row>
                : null}
                <Row className="py-3">
                  <GeoTargetsField
                    lineItemId={createMode ? null : entityId}
                    formState={formState}
                    initialFormData={initialFormData}
                    setValue={setValue}
                  />
                </Row>
                <Row className="py-3">
                  <Col>
                    <DomainTargetsField formState={formState} initialFormData={initialFormData} setValue={setValue} />
                  </Col>
                </Row>
                {["STANDARD_DISPLAY", "VIDEO", "DISPLAY"].includes(formState.inventoryType) ?
                  <Row>
                    <Col>
                      <IabCategoriesField
                        lineItemId={createMode ? null : entityId}
                        formState={formState}
                        initialFormData={initialFormData}
                        setValue={setValue}
                      />
                    </Col>
                  </Row>
                : null}
                {["STANDARD_DISPLAY", "AMAZON_MOBILE_DISPLAY", "DISPLAY"].includes(formState.inventoryType) ?
                  <>
                    <Row className="py-3">
                      <Col>
                        <ContextualProductTargetsField
                          lineItemId={createMode ? null : entityId}
                          formState={formState}
                          initialFormData={initialFormData}
                          setValue={setValue}
                        />
                      </Col>
                    </Row>
                    <Row className="py-3">
                      <Col xs={12} lg={8} xl={fieldColumnWidth}>
                        <Form.Group>
                          <CheckboxField
                            label={t(($) => $.dsp["Auto-target pages of products related to your advertised products"])}
                            fieldName={"enableRelatedProductsTargeting"}
                            control={control}
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row className="py-3">
                      <Col>
                        <ContextualCategoryTargetsField
                          lineItemId={createMode ? null : entityId}
                          formState={formState}
                          initialFormData={initialFormData}
                          setValue={setValue}
                        />
                      </Col>
                    </Row>
                    <Row className="py-3">
                      <Col>
                        <ContextualKeywordTargetsField
                          lineItemId={createMode ? null : entityId}
                          formState={formState}
                          setValue={setValue}
                        />
                      </Col>
                    </Row>
                  </>
                : null}

                {formState.targetingSettings?.amazonViewability?.viewabilityTier !== "ALL_TIERS" && (
                  <Row className="py-3">
                    <Col xs={12} lg={8} xl={fieldColumnWidth}>
                      <Form.Group>
                        <CheckboxField
                          label={t(($) => $.dsp["Include unmeasurable impressions"])}
                          fieldName={"targetingSettings.amazonViewability.includeUnmeasurableImpressions"}
                          control={control}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                )}
                <Row className="py-3">
                  <Col xs={12} lg={8} xl={fieldColumnWidth}>
                    <SelectField
                      label={t(($) => $.dsp["Time Zone Type"])}
                      options={TIME_ZONE_TYPE_OPTIONS}
                      fieldName={"targetingSettings.timeZoneType"}
                      control={control}
                    />
                  </Col>
                </Row>
                {(
                  ["STANDARD_DISPLAY", "AMAZON_MOBILE_DISPLAY"].includes(formState.inventoryType) ||
                  displayInventoryTypeWithMobile
                ) ?
                  <Row className="py-3">
                    <Col xs={12} lg={8} xl={fieldColumnWidth}>
                      <SelectField
                        label={t(($) => $.dsp["Mobile OS"])}
                        options={MOBILE_OS_OPTIONS}
                        fieldName={"mobileOperatingSystem"}
                        control={control}
                      />
                    </Col>
                  </Row>
                : null}
                {["AAP_MOBILE_APP"].includes(formState.inventoryType) || displayInventoryTypeWithMobile ?
                  <>
                    <Row className="py-3">
                      <Col xs={12} lg={11}>
                        <RadioGroupField
                          label={t(($) => $.dsp["Devices"])}
                          options={PHYSICAL_DEVICE_OPTIONS}
                          fieldName={"deviceTargets"}
                          control={control}
                          deriveFieldValue={(value) => {
                            return formState.deviceTargets?.includes(value);
                          }}
                          onChange={(value, checked) => {
                            let updatedDevices = formState.deviceTargets ?? [];

                            if (checked) {
                              if (value === "ALL") {
                                updatedDevices = ["ALL"];
                              } else {
                                updatedDevices.push(value);
                                updatedDevices = updatedDevices?.filter((device) => device !== "ALL");
                              }
                            } else {
                              updatedDevices = updatedDevices.filter((device) => device !== value);
                            }

                            setValue("deviceTargets", updatedDevices);
                          }}
                        />
                      </Col>
                    </Row>
                    <Row className="py-3">
                      <Col xs={12} lg={8} xl={fieldColumnWidth}>
                        <RadioGroupField
                          label={t(($) => $.dsp["Device Orientation"])}
                          options={DEVICE_ORIENTATION_OPTIONS}
                          fieldName={"deviceOrientationTarget"}
                          control={control}
                          onChange={(value) => {
                            setValue("deviceOrientationTarget", value);
                          }}
                        />
                      </Col>
                    </Row>
                  </>
                : null}
                <Row className="py-3">
                  <Col xs={12} lg={8} xl={fieldColumnWidth}>
                    <SelectField
                      label={t(($) => $.dsp["User Location"])}
                      options={USER_LOCATION_OPTIONS}
                      fieldName={"targetingSettings.userLocation"}
                      control={control}
                    />
                  </Col>
                </Row>

                <Row className="py-3">
                  <Col xs={12} lg={8} xl={fieldColumnWidth}>
                    <SelectField
                      label={t(($) => $.dsp["User Location Signal"])}
                      options={USER_LOCATION_SIGNAL_OPTIONS}
                      fieldName={"targetingSettings.userLocationSignal"}
                      control={control}
                    />
                  </Col>
                </Row>
                <Row className="py-3">
                  <Col xs={12} lg={8} xl={fieldColumnWidth}>
                    <Form.Group>
                      <CheckboxField
                        label={t(($) => $.dsp["Enable Language Targeting"])}
                        fieldName={"targetingSettings.enableLanguageTargeting"}
                        control={control}
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </Row>
              {/* Inventory */}
              <Row className="py-3 dsp-form-section" ref={inventorySectionRef}>
                <Form.Label className="fw-bold fs-2 px-2">{t(($) => $.dsp["Inventory"])}</Form.Label>
                {errors.inventoryCount ?
                  <span className={`text-danger ${css({ marginTop: ".5rem", textWrap: "nowrap", fontSize: "12px" })}`}>
                    {errors.inventoryCount.message}
                  </span>
                : null}
                <Row className="py-3">
                  <Col xs={12} lg={8} xl={fieldColumnWidth}>
                    <AmazonInventoriesField
                      lineItemId={createMode ? null : entityId}
                      formState={formState}
                      setValue={setValue}
                      control={control}
                      combinedInventoryCount={combinedInventoryCount}
                    />
                  </Col>
                </Row>
                <Row className="py-3">
                  <Col xs={12} lg={8} xl={fieldColumnWidth}>
                    <AmazonPublisherDirectInventoriesField
                      lineItemId={createMode ? null : entityId}
                      formState={formState}
                      setValue={setValue}
                      control={control}
                      combinedInventoryCount={combinedInventoryCount}
                    />
                  </Col>
                </Row>

                <Row className="py-3">
                  <Col>
                    <ThirdPartyInventoriesField
                      lineItemId={createMode ? null : entityId}
                      formState={formState}
                      setValue={setValue}
                      control={control}
                      combinedInventoryCount={combinedInventoryCount}
                    />
                  </Col>
                </Row>
              </Row>

              {/* Creatives */}
              <Row className="py-3 dsp-form-section" ref={creativesSectionRef}>
                <Form.Label className="fw-bold fs-2 px-2">{t(($) => $.dsp["Creatives"])}</Form.Label>
                <Row className="py-3">
                  <Col>
                    <CreativeSelector
                      selectedCreatives={
                        formState.creativeAssociations?.map((association) => association.ad_creative_id) ?? []
                      }
                      setSelectedCreatives={(creativeAssocationIds) => {
                        const updatedCreativeAssocationSpec = creativeAssocationIds?.map((creativeId) => {
                          const existingCreativeSpec = formState?.creativeAssociations?.find(
                            (association) => association.ad_creative_id === creativeId,
                          );

                          if (existingCreativeSpec) {
                            return existingCreativeSpec;
                          }

                          return {
                            state: "ENABLED",
                            ad_creative_id: creativeId,
                            weight: 100,
                          };
                        });

                        setValue("creativeAssociations", updatedCreativeAssocationSpec, { shouldDirty: true });
                      }}
                    />
                  </Col>
                </Row>
              </Row>

              <Row className="pt-4 pb-5 mb-5">
                <Col
                  xs={12}
                  lg={12}
                  className={`d-flex align-items-center justify-content-center gap-5 fs-5 pb-5 mb-5`}
                >
                  <Button
                    variant={"secondary"}
                    onPress={() => {
                      onCancel();
                      reset(formatLineItemFormData(initialFormData));
                    }}
                  >
                    {t(($) => $.dsp["Cancel"])}
                  </Button>

                  <Button
                    onPress={() => {
                      if (createMode || cloneMode) {
                        createLineItem.mutate();
                      } else {
                        updateLineItem.mutate();
                      }
                    }}
                    isDisabled={Object.keys(errors).length || !validateMandatoryLineItemFormFields(formState)}
                    isPending={updateLineItem.isPending || createLineItem.isPending}
                  >
                    {createMode || cloneMode ? t(($) => $.dsp["Create"]) : t(($) => $.dsp["Update"])}
                  </Button>
                </Col>
              </Row>
            </form>
          </Col>
        </Row>
      }
    </>
  );
};

export default DSPLineItemForm;
